import React, { forwardRef } from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import type { colors } from '../../../theme/axiom-theme';
import { ColorUtil } from '../../../utils/color-util';

import { useResizableColumns } from './ResizableColumnsContext';

export type ResizableColumnsItemEdge = 'left' | 'right';

export type ResizableColumnsItemProps = {
  /**
   * The column's own surface. A class rather than an inline style, so a theme that re-emits this
   * sheet against its own tokens reaches it - which an inline color could not be.
   */
  background?: keyof typeof colors;
  children?: React.ReactNode;
  /**
   * Takes this column out of the reader's reach: neither divider beside it is drawn, so it keeps
   * its width however the rest of the run is dragged. For a column whose width is a decision rather
   * than a preference - a strip holding one control, a panel a toggle opens and closes.
   *
   * A column measured in `px` is already out of reach and needs no say here.
   */
  disableResize?: boolean;
  /**
   * An edge this column casts a shadow along, saying it stands in front of what is beside it.
   *
   * Only ever one edge: a column shadowed on both sides reads as floating rather than as the front
   * of a stack, and which side it is depends on what it is in front of.
   */
  edgeShadow?: ResizableColumnsItemEdge;
  /**
   * Turns that shadow inward, so the column reads as the thing behind instead of the thing in
   * front - which is what a panel beside a closed drawer is.
   *
   * Both shadows are drawn in either state and only their weight changes, so the two ease into one
   * another as whatever they are reporting on opens or closes.
   */
  edgeShadowInset?: boolean;
  /**
   * How long the edge shadow takes to ease. **The group's own `duration` by default**, so the
   * shadow reports on the width change over exactly as long as the width takes - set it here only
   * for a column that should keep its own clock. Nothing without `edgeShadow`.
   */
  edgeShadowDuration?: string;
  name?: string;
  /**
   * Makes this column a size container, so what is inside it can be sized against its height -
   * `100cqh` for "as tall as the column", rather than the viewport less whatever sits above it.
   *
   * It applies size containment, so the column's own size must not come from its contents. That
   * holds for a track this container has sized, and does not for `overflow="visible"` - a column
   * whose content paints outside it is the one case containment would break.
   */
  sizeContainer?: boolean;
  /**
   * `visible` for a column whose content is *meant* to paint outside it - a drawer that slides out
   * over its neighbours, say. Only reach for it in that case: it gives up the scrolling, because a
   * box cannot both clip an overflow to scroll it and let one escape.
   */
  overflow?: 'scroll' | 'visible';
};

/**
 * One column of a `ResizableColumns`.
 *
 * **It scrolls its own overflow, and needs nothing said to it.** A column is exactly as tall as the
 * space the container was given, so content taller than that has nowhere else to go; content
 * shorter than it never shows a scrollbar. `overflow` exists only for the column that has to break
 * that rule.
 *
 * Its width is the container's business - dragged by the reader, or set by whatever holds it.
 */
export const ResizableColumnsItem = forwardRef<
  HTMLDivElement,
  ResizableColumnsItemProps
>(
  (
    {
      background,
      children,
      edgeShadow,
      edgeShadowDuration,
      edgeShadowInset,
      name,
      overflow = 'scroll',
      sizeContainer = false,
    },
    ref
  ) => {
    const { duration } = useResizableColumns();

    return (
      <div
        ref={ref}
        className={AttrsHelper.formatClassname(
          'resizable-columns-item',
          overflow === 'visible' && 'resizable-columns-item-visible',
          edgeShadow && `edge-shadow-${edgeShadow}`,
          edgeShadow && edgeShadowInset && 'edge-shadow-inset',
          sizeContainer && 'size-container',
          ColorUtil.tokenClass({ color: background, prefix: 'background' })
        )}
        data-test={name}
        style={{ transitionDuration: edgeShadowDuration ?? duration }}
      >
        {children}
      </div>
    );
  }
);
